import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { Location } from '~common/react/Location';
import { imgPath } from '~common/utilities/ImgUrl';
import { ISanitizer } from '~commonInfra/utils/Sanitize';
import { selectSelectedContentItems } from '~selectors/ContentSelectors';
import {
  CompositeSessionInfoSelectors,
  SessionSiteSelectors,
  SessionUserSelectors,
} from '~selectors/SessionInfoSelectors';
import { selectSpaceInfo } from '~selectors/SpaceInfoSelectors';
import { callCollectionsReorder } from '~src/app/actions/callCollectionsReorder';
import { clearSpaceContentCountsAction } from '~src/app/actions/spaces/ContentCountsActions';
import { showContentTypes } from '~src/app/actions/spaces/ContentExplorerFilterActions';
import { requestSpaceContents } from '~src/app/actions/spaces/RequestSpaceContentsActions';
import { selectCurrentlyApplicableContentTypes, selectDefaultContentTypeFilterValues } from '~src/app/selectors/ContentFilterSelectors';
import { Messages } from '~src/codegen/Localize';
import { getCollectionSpaceContentInOrder } from '~src/contentDefinitionInterfaces/ContentDefinitionsLists';
import { SpaceContainerStyles } from '~src/style/SpaceContainerStyles';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { ExplorableItemType } from '~src/ts/models/ContentType';
import { IContentInfo } from '~src/ts/models/IContentInfo';
import { ISpaceItemDetailInfo } from '~src/ts/models/ISpaceItemDetailInfo';

import { shareContent } from '../../actions/spaces/ShareContentAction';
import { AppState } from '../../state/AppState';
import { SpaceInfo, SpaceType } from '../../state/SpaceInfo';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { ExplorerEmptyStateProps } from '../Components/ExplorerEmpyState';
import { ConnectedExplorer } from '../Explorer/ConnectedExplorer';
import { ExplorerStyles } from '../Explorer/ExplorerStyles';
import { ExplorerToolbarTestIds } from '../Explorer/ExplorerToolbar';
import { FilterByTypeDropdown } from '../Explorer/FilterByTypeDropdown';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { CollectionSpaceHeader } from './CollectionSpaceHeader';
import { CollectionsUsageStats } from './ResourceQuery/CollectionsResourceQuery';

export interface CollectionSpaceStateProps {
  readonly spaceInfo: SpaceInfo;
  readonly isPublicCollectionsEnabled: boolean;
  readonly selectedContentItemsCount: number;
  readonly isAdmin: boolean;
  readonly currentUserId: string;
  readonly contentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly defaultContentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly allowDragAndDrop: boolean;
}

interface CollectionSpaceDispatchProps {
  readonly shareContent: (content: IContentInfo) => void;
  readonly filterSelected: (selectedItems: ArrayLike<ExplorableItemType>) => void;
  readonly requestSpaceContents: () => void;
  readonly clearContentCounts: (spaceType: SpaceType) => void;
  readonly callCollectionsReorder: (fromIndex: number, toIndex: number, spaceInfo: SpaceInfo) => void;
}

export interface CollectionSpaceOwnProps {
  readonly urls: SpacesSiteUrls;
  readonly reloadSpace: () => void;
  readonly sanitizer: ISanitizer;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
}

export type CollectionSpaceProps = CollectionSpaceStateProps & CollectionSpaceDispatchProps & CollectionSpaceOwnProps;

export const CollectionShareTestId = 'collection-share';
export const CollectionPrivateHeader = 'collection-private-header';

export class CollectionSpace extends React.PureComponent<CollectionSpaceProps> {
  public componentDidMount() {
    if (this.props.spaceInfo.item) {
      CollectionsUsageStats.markViewed(this.props.spaceInfo.item.id);
    }
  }

  private onClickShareButton = (): void => {
    this.props.shareContent(this.props.spaceInfo.item as IContentInfo);
  }

  private onNoContentAction = (): void => {
    Location.instance.setUrl(this.props.urls.exploreUrl());
  }

  private onNoPermissionedContentAction = (): void => {
    Location.instance.setUrl(this.props.urls.collectionsUrl());
  }

  public render(): JSX.Element {
    return (
      <div className={SpaceContainerStyles.SpaceContainer}>
        <div className={SpaceContainerStyles.MastheadArea}>
          {this.mastheadElement()}
        </div>
        <div className={SpaceContainerStyles.SubplaceArea}>
          <ConnectedExplorer
            supportedDisplayModes={['list', 'thumbnail']}
            toolbarProps={{
              supportFilterPanel: FeatureFlags.collectionsGAEnabled,
              toolbarContentFilterControl: FeatureFlags.collectionsGAEnabled? this.contentFilterControl() : undefined,
              toolbarVisibility: 'enabled'
            }}
            emptyStateProps={this.emptyStateProps()}
            reorderItemAction={FeatureFlags.reorderCollectionsEnabled ? this.props.callCollectionsReorder : null}
            allowDragAndDrop={FeatureFlags.reorderCollectionsEnabled && this.props.allowDragAndDrop}
            reorderAccessibilityInstructions = {Messages.collectionReorderAccessibilityInstructions()}
          />
        </div>
      </div>
    );
  }

  private contentFilterControl(): JSX.Element {
    const collectionSpaceContentInOrder = getCollectionSpaceContentInOrder();

    return (
      <React.Fragment>
        <span id={ExplorerToolbarTestIds.FilterByTypeDropdownButtonLabel} className={ExplorerStyles.ToolbarDropdownLabel}>
           {Messages.filterByTypeDropdown_label()}
        </span>
          <FilterByTypeDropdown
            filterSelected={this.props.filterSelected}
            contentFilter={this.props.contentTypeFilterValues}
            defaultContentTypeFilterValues={this.props.defaultContentTypeFilterValues}
            clearContentCounts={this.props.clearContentCounts}
            spaceType={this.props.spaceInfo.spaceType}
            contentItemDefinitions={collectionSpaceContentInOrder}
          />
      </React.Fragment>
    );
  }

  private emptyStateProps = (): ExplorerEmptyStateProps => {
    const collectionItem = this.props.spaceInfo.item as ISpaceItemDetailInfo<'collection'>;
    if (collectionItem.totalItemCount > 0 && collectionItem.permissionedItemCount === 0) {
      // special empty state if user just doesn't have access to the items in the collection
      return {
        primaryMessage: Messages.collectionSpaceEmtpyStateNoAccessToItemsMessage()
      };
    }

    return {
      iconUrl: imgPath('collectionSpace_EmptyState.svg'),
      iconStyleOverrides: { width: '87px', height: '82px' },
      primaryMessage: Messages.collectionSpaceEmptyStatePrimaryMessage(),
      secondaryMessage: Messages.collectionSpaceEmptyStateSecondaryMessage(),
      onActionButton: this.onNoContentAction,
      actionButtonMessage: Messages.emptyStateCallToAction()
    };
  }

  private mastheadElement(): React.ReactNode {
    const collectionItem = this.props.spaceInfo.item as ISpaceItemDetailInfo<'collection'>;
    return <CollectionSpaceHeader
      item={collectionItem}
      addToFavorites={this.props.addToFavorites}
      removeFromFavorites={this.props.removeFromFavorites}
      urls={this.props.urls}
      onShare={this.onClickShareButton}
      isPublicCollectionsEnabled={this.props.isPublicCollectionsEnabled}
      selectedContentItemsCount={this.props.selectedContentItemsCount}
      isAdminOrOwner={this.props.isAdmin || (this.props.currentUserId === collectionItem.userId)}
      isOwner={this.props.currentUserId === collectionItem.userId}
      sanitizer={this.props.sanitizer}
      reloadSpace={this.props.reloadSpace}
    />;
  }
}

const mapStateToProps: MapStateToProps<CollectionSpaceStateProps> = (state: AppState) => ({
  spaceInfo: selectSpaceInfo(state),
  isPublicCollectionsEnabled: SessionSiteSelectors.isPublicCollectionsEnabled(state),
  selectedContentItemsCount: _.values(selectSelectedContentItems(state)).length,
  isAdmin: CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector(state),
  currentUserId: SessionUserSelectors.sessionUserId(state),
  contentTypeFilterValues: selectCurrentlyApplicableContentTypes(state),
  defaultContentTypeFilterValues: selectDefaultContentTypeFilterValues(state),
  allowDragAndDrop: CompositeSessionInfoSelectors.currentUserIsAdminOrOwnerSelector((state.spaceInfo.item as ISpaceItemDetailInfo<'collection'>).owner)(state)
});

const mapDispatchToProps: MapDispatchToProps<CollectionSpaceDispatchProps> = (dispatch) => bindActionCreators({
  shareContent: shareContent,
  filterSelected: showContentTypes,
  requestSpaceContents: requestSpaceContents,
  clearContentCounts: clearSpaceContentCountsAction,
  callCollectionsReorder: callCollectionsReorder
}, dispatch);

export const ConnectedCollectionSpace = connect(mapStateToProps, mapDispatchToProps)(CollectionSpace);
